import tailorService from '../../../services/tailorService';
import filterService from '../../../services/filterService';

import {
    setTailorLoading,
    setTailorError,
    setTailorList,
    setTailorCurrent,
    setTailorPagination,
    setTailorSort,
    setTailorFilters,
    setTailorDefaultFilter,
    setTailorSettings,
    clearTailorCurrent,
    clearTailorError,
    setTailorFilterLoading,
    changeTailorFilterCondition,
    addTailorFilterCondition,
    updateTailorFilterConditionRule,
    updateTailorFilterConditionValue,
    removeTailorFilterCondition,
    setTailorSelected,
    resetTailorFiltersAndPagination
} from './tailorSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().tailor.isSettingsInitialized) {
            dispatch(setTailorLoading(true));
            const response = await tailorService.getSettings();

            // Set the settings
            dispatch(setTailorSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setTailorDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setTailorError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setTailorLoading(false));
    }
};

export const fetchTailors = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().tailor;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setTailorLoading(true));
        const response = await tailorService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setTailorList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchTailors:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setTailorError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setTailorLoading(false));
    }
};

export const fetchTailor = (id) => async (dispatch) => {
    try {
        dispatch(setTailorLoading(true));
        const response = await tailorService.getOne(id);
        dispatch(setTailorCurrent(response.data));
    } catch (error) {
        dispatch(setTailorError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setTailorLoading(false));
    }
};

export const createTailor = (data) => async (dispatch) => {
    try {
        const response = await tailorService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateTailor = (id, data) => async (dispatch) => {
    try {
        const response = await tailorService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteTailor = (id) => async (dispatch) => {
    try {
        dispatch(setTailorLoading(true));
        const response = await tailorService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setTailorError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setTailorLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setTailorPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setTailorSort(sort));
};

export const searchTailors = (query) => (dispatch, getState) => {
    const currentFilters = getState().tailor.filters;
    dispatch(setTailorFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setTailorDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeTailorFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addTailorFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateTailorFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateTailorFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeTailorFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setTailorFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().tailor.settings;
            dispatch(setTailorSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setTailorFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setTailorFilterLoading(false));
        dispatch(setTailorError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setTailorSelected(selectedRows));
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetTailorFiltersAndPagination());
};